import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DragHandleHorizontalIcon, PersonIcon } from '@radix-ui/react-icons';
import { Avatar, ElevatedButton } from '@producer/ui-kit';
import styled from 'styled-components';

import { context } from '../../../app/context';
import { TaskCollection } from './task-collection';
import { SaveTaskDto, Step, Task, TaskLevel, TaskStatus } from '../../../app/entities';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { ToolBar } from '../../../components/toolbar/toolbar';
import { ProjectPriority, ProjectPriorityByValue } from '../../../app/entities';
import { PriorityIcon } from '../../process/components/priority-selector';
import { StatusSelector } from '../../process/components/status-selector';
import { Member } from '../../../app/entities';
import { CustomAvatar } from './styled-tasks-components';
import { taskGroupingStore } from '../../../core/stores/task-store';
import { SpaceTasksFilters } from './space-tasks-filters/space-tasks-filters';
import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { ProjectTasksFilters } from './project-tasks-filters/project-tasks-filters';

type TaskListProps = {
  tasks: Task[];
  taskLevel: TaskLevel;
  onClearFilters?: () => void;

  /**
   * Override the default navigation when clicking on a task
   * @param taskId
   */
  onSelectTask?: (taskId: string) => void;

  /**
   * Default values to be used when creating task in this list
   *
   * It will be combined with the grouping defaultValues, the grouping default values
   * will have higher priority
   */
  defaultValues?: Partial<SaveTaskDto>;
};

type Group = {
  name: string;
  value: any;
  tasks: Task[];
};

type SortedGroups = {
  [key: string]: Group;
};

interface GroupByOption {
  key: string;
  label: string;
  defaultKey?: any;
  defaultValue?: string;
  getValues?: () => any;
  getDefaultValue: (value: any) => any;
  getTitle: (task: Task) => string;
  getIcon?: (value: any) => any;
  sortBy: (a: Group, b: Group) => any;
}

const baseTaskOptions: GroupByOption[] = [
  {
    key: 'status',
    defaultValue: TaskStatus.Todo,
    label: 'Status',
    getTitle: (task: Task) => Task.StatusMapper[task.status].title,
    getDefaultValue: (status: string) => ({ status }),
    getIcon: (value: TaskStatus) => (
      <StatusSelector status={value} disableTooltip isViewOnly statusMap={Task.StatusMapper} />
    ),
    sortBy: (a: Group, b: Group) => {
      if (!a.value) return -1;
      if (!b.value) return 1;
      return a.value > b.value ? -1 : 1;
    },
  },
  {
    key: 'assigneeId',
    label: 'Assignee',
    defaultKey: 'no-assignee',
    defaultValue: undefined,
    getDefaultValue: (assigneeId: string) => ({ assigneeId }),
    getTitle: (task: Task) => task.assignee?.fullName ?? 'No assignee',
    getIcon: (assigneeId: string) => {
      const assignee = Member.getOne(assigneeId)! as Member;

      if (!assignee) {
        return (
          <AvatarWrapper>
            <CustomAvatar>
              <PersonIcon />
            </CustomAvatar>
          </AvatarWrapper>
        );
      }

      return (
        <PrioritySelectorWrapper>
          <Avatar size="small" theme="dark" initials={assignee.initials} src={assignee.avatar} />
        </PrioritySelectorWrapper>
      );
    },
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
  {
    key: 'priority',
    label: 'Priority',
    defaultValue: '',
    getDefaultValue: (priority: string) => ({ priority }),
    getTitle: (task: Task) =>
      task.priority ? ProjectPriorityByValue[task.priority] : 'No priority',
    getIcon: (value: ProjectPriority) => (
      <PrioritySelectorWrapper>
        <PriorityIcon priority={value} />
      </PrioritySelectorWrapper>
    ),
    sortBy: (a: Group, b: Group) => {
      if (!a.value) return -1;
      if (!b.value) return 1;
      return a.value > b.value ? -1 : 1;
    },
  },
  {
    key: 'tag',
    label: 'Tag',
    defaultValue: '',
    getValues: () => {
      const space = context.space;
      return space.tags?.filter((tag) => tag.target === 'task');
    },
    getDefaultValue: (tagId: string) => ({ tagsIds: [tagId] }),
    getTitle: (task: Task) => (task.priority ? ProjectPriorityByValue[task.priority] : 'No tag'),
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
];

const projectTaskOptions: GroupByOption[] = [
  ...baseTaskOptions,
  {
    key: 'stepId',
    label: 'Step',
    defaultKey: 'no-step',
    defaultValue: undefined,
    getDefaultValue: (stepId: string) => {
      const step = Step.getOne(stepId);

      return {
        stepId,
        dueDate: step?.dueDate,
      };
    },
    getTitle: (task: Task) => task.step?.name ?? 'No step',
    getIcon: () => null,
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
];

const spaceTaskOptions: GroupByOption[] = [
  ...baseTaskOptions,
  {
    key: 'projectId',
    label: 'Project',
    defaultKey: 'no-project',
    defaultValue: undefined,
    getDefaultValue: (projectId: string) => ({ projectId }),
    getTitle: (task: Task) => task.project?.name ?? 'No project',
    getIcon: () => null,
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
];

export const TaskList: React.FC<TaskListProps> = observer(
  ({ tasks, taskLevel, onClearFilters, onSelectTask, defaultValues }) => {
    const options = taskLevel === 'space' ? spaceTaskOptions : projectTaskOptions;

    const [groups, setGroups] = useState<SortedGroups>();
    const groupBy = options.find((el) => el.key === taskGroupingStore.groupBy) || options[0];

    useEffect(() => {
      if (!tasks) return;

      const groups: SortedGroups = {};

      if (groupBy.getValues) {
        /**
         * Only works for tags atm
         */
        groupBy.getValues().forEach((groupValue: any) => {
          groups[groupValue._id] = {
            name: groupValue.text,
            value: groupValue._id,
            tasks: tasks.filter((task) => task.tagsIds.includes(groupValue._id)),
          };
        });

        if (tasks.some((task) => task.tagsIds.length === 0)) {
          groups['no-tag'] = {
            name: 'No tag',
            value: undefined,
            tasks: tasks.filter((task) => task.tagsIds.length === 0),
          };
        }
      } else {
        tasks.forEach((task) => {
          const groupByKey = (task[groupBy.key as keyof Task] ||
            groupBy.defaultKey) as keyof typeof groups;

          const value = (task[groupBy.key as keyof Task] ||
            groupBy.defaultValue) as keyof typeof groups;

          if (!groups[groupByKey]) {
            groups[groupByKey] = {
              name: groupBy.getTitle(task),
              value,
              tasks: [],
            };
          }

          groups[groupByKey].tasks.push(task);
        });
      }

      setGroups(groups);
    }, [tasks, groupBy]);

    const items = options.map((option) => ({
      title: option.label,
      onCheckedChange: () => {
        if (taskGroupingStore.groupBy !== option.key) {
          taskGroupingStore.setGroupBy(option.key);
        }
      },
      isChecked: groupBy.key === option.key,
    }));

    return (
      <>
        <ToolBar>
          {taskLevel === 'space' ? <SpaceTasksFilters /> : <ProjectTasksFilters />}

          <div />

          <DropdownMenu
            items={items}
            trigger={
              <div>
                <ElevatedButton text="Grouping" icon={<DragHandleHorizontalIcon />} />
              </div>
            }
          />
        </ToolBar>

        <Container>
          {tasks?.length ? (
            groups &&
            Object.values<Group>(groups)
              .sort(groupBy.sortBy)
              .map((group: any) =>
                group.tasks.length ? (
                  <TaskCollection
                    key={`${group.value}-${group.name}-${group.tasks.length}`}
                    title={group.name}
                    value={group.value}
                    tasks={group.tasks}
                    getIcon={groupBy.getIcon}
                    defaultValues={{...defaultValues, ...groupBy.getDefaultValue(group.value)}}
                    taskLevel={taskLevel}
                    onSelectTask={onSelectTask}
                  />
                ) : null,
              )
          ) : (
            <PagePlaceholder
              title="No tasks matching the filters"
              actions={[
                <ElevatedButton
                  key="clear-filters"
                  text="Clear filters"
                  onClick={onClearFilters}
                />,
              ]}
              backgroundImgUrl="/images/order-from-chaos.svg"
              fullWidth
            />
          )}
        </Container>
      </>
    );
  },
);

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  overflow-x: clip;
  container-type: inline-size;
`;

const PrioritySelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  width: 2.4rem;
  height: 2.4rem;
  margin-inline-end: 0.8rem;
  background-color: #25272d;
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0.8rem;
`;
